import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { ButtonItem, MenuGroup } from '@atlaskit/menu';
import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseField } from '@databases/types/database';
import { type DatabaseCalculationType } from '@databases/types/database/view';
import { getFieldCalculationType } from '@databases/utils/database/utils';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { DatabaseCalculationLabel } from './DatabaseCalculationLabel';
import { getCalculationOptionsForFieldType } from './quickCalcUtils';

interface QuickCalcOptionProps {
  field: DatabaseField;
  handleOnClick: (databaseCalculationType?: DatabaseCalculationType) => void;
  calculations?: Record<string, DatabaseCalculationType>;
  contentId: string;
}
export const QuickCalcOptions: React.FC<QuickCalcOptionProps> = ({
  field,
  handleOnClick,
  calculations,
  contentId,
}) => {
  const currentCalculationType = getFieldCalculationType(field.id, calculations);
  const { formatMessage } = useIntl();
  const state = defaultStoreUtils.useStore().getState();
  const dispatch = defaultStoreUtils.useDispatch();

  const checkIcon = (
    <CheckMarkIcon
      label={formatMessage(messages['quick.calculations.check-icon'])}
      color={token('color.text.brand', '#0C66E4')}
    />
  );
  const items = getCalculationOptionsForFieldType(field, state, contentId, dispatch).map(
    (calculationType) => {
      return (
        <ButtonItem
          key={calculationType}
          iconBefore={
            currentCalculationType ? (
              <Inline
                xcss={xcss({
                  visibility:
                    currentCalculationType === calculationType ? 'visible' : 'hidden',
                })}
              >
                {checkIcon}
              </Inline>
            ) : null
          }
          onClick={() => {
            handleOnClick(calculationType);
          }}
        >
          <DatabaseCalculationLabel type={calculationType} fieldType={field.type} />
        </ButtonItem>
      );
    }
  );

  if (currentCalculationType) {
    const NoneOption = (
      <ButtonItem
        key={'none'}
        iconBefore={
          <Inline
            xcss={xcss({
              visibility: currentCalculationType ? 'hidden' : 'visible',
            })}
          >
            {checkIcon}
          </Inline>
        }
        onClick={() => {
          handleOnClick(undefined);
        }}
      >
        {formatMessage(messages['quick.calculations.calculation-type-none'])}
      </ButtonItem>
    );

    return <MenuGroup spacing="compact">{[NoneOption, ...items]}</MenuGroup>;
  } else {
    return <MenuGroup spacing="compact">{items}</MenuGroup>;
  }
};
